import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

export interface TocItem {
  level: 2 | 3;
  text: string;
  id: string;
}

interface TableOfContentsProps {
  items: TocItem[];
  className?: string;
}

/**
 * Sticky scroll-spy table of contents. Highlights the active heading as the
 * reader scrolls. Items link to anchors in the rendered markdown.
 */
export function TableOfContents({ items, className }: TableOfContentsProps) {
  const [active, setActive] = useState<string | null>(items[0]?.id ?? null);

  useEffect(() => {
    if (items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry closest to the top of the viewport that is intersecting.
        const intersecting = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (intersecting[0]?.target instanceof HTMLElement) {
          setActive(intersecting[0].target.id);
        }
      },
      { rootMargin: '-96px 0px -66% 0px', threshold: [0, 1] },
    );

    items.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  return (
    <nav aria-label="Table of contents" className={cn('text-sm', className)}>
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-fg-subtle">On this page</p>
      <ul className="space-y-2 border-l border-border">
        {items.map((item) => (
          <li
            key={item.id}
            className={cn(
              item.level === 3 && 'pl-4',
            )}
          >
            <a
              href={`#${item.id}`}
              className={cn(
                '-ml-px block border-l py-1 pl-4 transition-colors',
                active === item.id
                  ? 'border-gold-500 text-gold-500'
                  : 'border-transparent text-fg-muted hover:text-fg',
              )}
            >
              {item.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}